import { clone as _clone, merge as _merge, toPath as _toPath } from "lodash-es";

import { isInteger, isObject } from "@/utils";

/**
 * Deeply get a value from an object via its path.
 */
export function getIn(obj: any, key: string | Array<string>, def?: any, p = 0) {
	const path = _toPath(key);
	while (obj && p < path.length) {
		obj = obj[path[p++]];
	}
	return obj === undefined ? def : obj;
}

export const merge = _merge;

/**
 * Deeply set a value from in object via it's path. If the value at `path`
 * has changed, return a shallow copy of obj with `value` set at `path`.
 * If `value` has not changed, return the original `obj`.
 *
 * Existing objects / arrays along `path` are also shallow copied. Sibling
 * objects along path retain the same internal js reference. Since new
 * objects / arrays are only created along `path`, we can test if anything
 * changed in a nested structure by comparing the object's reference in
 * the old and new object, similar to how russian doll cache invalidation
 * works.
 *
 * In earlier versions of this function, which used cloneDeep, there were
 * issues whereby settings a nested value would mutate the parent
 * instead of creating a new object. `clone` avoids that bug making a
 * shallow copy of the objects along the update path
 * so no object is mutated in place.
 *
 * Before changing this function, please read through the following
 * discussions.
 *
 * @see https://github.com/developit/linkstate
 * @see https://github.com/jaredpalmer/formik/pull/123
 */
export function setIn(obj: any, path: string, value: any): any {
	const res: any = _clone(obj); // this keeps inheritance when obj is a class
	let resVal: any = res;
	let i = 0;
	const pathArray = _toPath(path);

	for (; i < pathArray.length - 1; i++) {
		const currentPath: string = pathArray[i];
		const currentObj: any = getIn(obj, pathArray.slice(0, i + 1));

		if (currentObj && (isObject(currentObj) || Array.isArray(currentObj))) {
			resVal = resVal[currentPath] = _clone(currentObj);
		} else {
			const nextPath: string = pathArray[i + 1];
			resVal = resVal[currentPath] = isInteger(nextPath) && Number(nextPath) >= 0 ? [] : {};
		}
	}

	// Return original object if new value is the same as current
	if ((i === 0 ? obj : resVal)[pathArray[i]] === value) {
		return obj;
	}

	if (value === undefined) {
		delete resVal[pathArray[i]];
	} else {
		resVal[pathArray[i]] = value;
	}

	// If the path array has a single element, the loop did not run.
	// Deleting on `resVal` had no effect in this scenario, so we delete on the result instead.
	if (i === 0 && value === undefined) {
		delete res[pathArray[i]];
	}

	return res;
}

/**
 * Recursively a set the same value for all keys and arrays nested object, cloning
 * @param object
 * @param value
 * @param visited
 * @param response
 */
function setNestedObjectValues<T>(
	object: any,
	value: any,
	visited: any = new WeakMap(),
	response: any = {},
): T {
	for (const k of Object.keys(object)) {
		const val = object[k];
		if (isObject(val)) {
			if (!visited.get(val)) {
				visited.set(val, true);
				// In order to keep array values consistent for both dot path  and
				// bracket syntax, we need to check if this is an array so that
				// this will output  { friends: [true] } and not { friends: { "0": true } }
				response[k] = Array.isArray(val) ? [] : {};
				setNestedObjectValues(val, value, visited, response[k]);
			}
		} else {
			response[k] = value;
		}
	}

	return response;
}
